import colors from '@lmc/common/constants'
import type { CarerSkill } from '@lmc/ts-common'
import { Popover, TableOLD } from '@lmc/ui-kit'
import moment from 'moment'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { Spinner } from '../../../../../components/generic'
import type { SkillOptionType } from '../CarerSkillsScreen'
import { BadgeType, CarerSkillsTableBadges, RenderRowCell } from './utils'

interface Props {
  skills: CarerSkill[]
  handleSkillDetails: (id: string) => void
  skillsFlattenOptions?: SkillOptionType[]
  loading?: boolean
  testId?: string
}

export function CarerSkillsTable({
  skills,
  handleSkillDetails,
  skillsFlattenOptions,
  loading,
  testId
}: Props): React.ReactElement {
  const { t } = useTranslation()

  if (loading) {
    return <Spinner data-testid={testId ? `${testId}-loading` : undefined} />
  }

  return (
    <TableOLD
      data-testid={testId}
      columns={[
        {
          key: 'skills',
          // deprecated Table component
          // @ts-ignore
          title: t(`skillsAndNeeds.table.skills`),
          cellType: 'custom',
          sortable: true,
          sortKey: 'skillsTemplateName'
        },
        {
          key: 'training',
          // deprecated Table component
          // @ts-ignore
          title: t(`skillsAndNeeds.table.training`),
          cellType: 'custom'
        },
        {
          key: 'achieved',
          // deprecated Table component
          // @ts-ignore
          title: t(`skillsAndNeeds.table.achieved`),
          cellType: 'custom'
        },
        {
          key: 'refresh',
          // deprecated Table component
          // @ts-ignore
          title: t(`skillsAndNeeds.table.refreshDue`),
          cellType: 'custom'
        }
      ]}
      onRow={(record: CarerSkill | unknown) => ({
        onClick: () => {
          handleSkillDetails((record as CarerSkill).id)
        }
      })}
      rows={skills?.map((carerSkill) => {
        const templateBySkillId = skillsFlattenOptions?.find(
          (option) => option.id === carerSkill.skill
        )

        return {
          key: carerSkill.id,
          id: carerSkill.id,
          skillsTemplateName: templateBySkillId?.name,
          skills: {
            renderCell: () => (
              <RenderRowCell classVariable={styles.cellRow}>
                {templateBySkillId?.name}
              </RenderRowCell>
            )
          },
          training: {
            renderCell: () => (
              <RenderRowCell classVariable={styles.cellRow}>
                {templateBySkillId?.template?.trainingName || '-'}
              </RenderRowCell>
            )
          },
          achieved: {
            renderCell: () => (
              <RenderRowCell classVariable={styles.cellRow}>
                {templateBySkillId?.template?.trainingRequired &&
                  carerSkill?.trainingCompletedDate &&
                  moment(carerSkill?.trainingCompletedDate).format(
                    'DD-MM-YYYY'
                  )}
                {templateBySkillId?.template?.trainingRequired &&
                  !carerSkill?.trainingCompletedDate &&
                  CarerSkillsTableBadges(BadgeType.NotSet, t)}
                {!templateBySkillId?.template?.trainingRequired &&
                  CarerSkillsTableBadges(undefined, t)}
              </RenderRowCell>
            )
          },
          refresh: {
            renderCell: () => {
              const today = new Date()

              const todayPlus30Days = moment(new Date()).add(1, 'M')

              const trainingWithDuration = moment(
                carerSkill?.trainingCompletedDate
              ).add(templateBySkillId?.template?.trainingValidFor, 'M')

              const isOverDue =
                trainingWithDuration.isBefore(today) &&
                templateBySkillId?.template?.trainingValidFor !== 0

              const isDueSoon =
                trainingWithDuration.isAfter(today) &&
                trainingWithDuration.isBefore(todayPlus30Days)

              const doesNotExpire =
                templateBySkillId?.template?.trainingValidFor === 0 ||
                !templateBySkillId?.template?.trainingValidFor

              return (
                <div style={styles.cellRow}>
                  {!doesNotExpire &&
                    !isOverDue &&
                    !isDueSoon &&
                    templateBySkillId?.template?.trainingRequired &&
                    carerSkill?.trainingCompletedDate &&
                    trainingWithDuration.format('DD-MM-YYYY')}

                  {isOverDue &&
                    templateBySkillId?.template?.trainingRequired &&
                    carerSkill?.trainingCompletedDate && (
                      <Popover
                        content={
                          <div style={styles.popover}>
                            Due: {trainingWithDuration.format('DD-MM-YYYY')}
                          </div>
                        }
                        placement="bottom"
                      >
                        <div>
                          {CarerSkillsTableBadges(BadgeType.Overdue, t)}
                        </div>
                      </Popover>
                    )}

                  {isDueSoon &&
                    templateBySkillId?.template?.trainingRequired &&
                    carerSkill?.trainingCompletedDate && (
                      <Popover
                        content={
                          <div style={styles.popover}>
                            Due: {trainingWithDuration.format('DD-MM-YYYY')}
                          </div>
                        }
                        placement="bottom"
                      >
                        <div>
                          {CarerSkillsTableBadges(BadgeType.DueSoon, t)}
                        </div>
                      </Popover>
                    )}

                  {!isDueSoon &&
                    !isOverDue &&
                    (!templateBySkillId?.template?.trainingRequired ||
                      doesNotExpire) &&
                    CarerSkillsTableBadges(undefined, t)}
                </div>
              )
            }
          }
        }
      })}
    />
  )
}

const styles = {
  logoIcon: {
    width: 26,
    height: 26
  },
  cellRow: {
    width: 'max-content',
    cursor: 'pointer'
  },
  popover: {
    padding: 10,
    backgroundColor: colors.black,
    borderRadius: 5,
    color: colors.white
  }
}

export default CarerSkillsTable
